/**
 * One board, one player: which of the panels mounted beside each other may sound.
 *
 * The arrange board draws the same bars twice -- as they were written and as the rules
 * re-wrote them -- and the point of drawing them twice is that they are compared by ear.
 * Compared means one after the other: pressing play on the arrangement is asking to hear
 * *that* version, not to hear it on top of the original. Two players sounding at once is
 * not a comparison, it is a third piece of music neither panel is showing.
 *
 * So the stage is held by one panel at a time. Taking it is what pressing play does, and
 * whoever held it stops. Nothing is ever released: a paused panel keeps the stage until
 * another panel asks for it, so pressing play again on the one you were just listening to
 * carries on where it stood instead of starting over.
 */

import { useCallback, useMemo, useState } from "react";

/** A panel's seat on the stage, as that panel sees it. */
export interface Stage {
  /** Whether this panel is the one that may sound. */
  readonly held: boolean;
  /** Take the stage, so that whoever held it stops. */
  take(): void;
}

/**
 * A stage for each of `names`, in the same order.
 *
 * `names` is read on every render and must be the same array each time -- a module-level
 * constant -- because the seats it answers with are what the panels watch for the order to
 * stop, and a fresh seat on every render would be a fresh order on every render.
 */
export function useStage(names: readonly string[]): readonly Stage[] {
  const [holder, setHolder] = useState<string | null>(null);
  const take = useCallback((name: string) => () => setHolder(name), []);
  return useMemo(
    () => names.map((name) => ({ held: holder === name, take: take(name) })),
    [names, holder, take],
  );
}
